import { getRequiredPasswordLength } from '@pioz/mlm-common';
import { sub } from 'date-fns';
import { z } from 'zod';

export interface PasswordValidationT {
  required: string;
  passwordLowercase: string;
  passwordUppercase: string;
  passwordNumber: string;
  passwordNoSpace: string;
  passwordLength: string;
  passwordSpecialChar: string;
}

export const password = (t: PasswordValidationT, config?: { checkSpecialChars?: boolean }) =>
  z
    .string()
    .min(1, t.required)
    .min(
      getRequiredPasswordLength(),
      t.passwordLength.replace('{length}', getRequiredPasswordLength() + ''),
    )
    .refine((value) => /[a-z]/.test(`${value}`), t.passwordLowercase)
    .refine((value) => /[A-Z]/.test(`${value}`), t.passwordUppercase)
    .refine((value) => /[0-9]/.test(`${value}`), t.passwordNumber)
    .refine((value) => !/[' ']/.test(`${value}`), t.passwordNoSpace)
    .refine((value) => {
      if (!config?.checkSpecialChars) {
        return true;
      }
      return /[!@#$%^&*()_+\-=]/.test(value);
    }, t.passwordSpecialChar);

export const pin = (t: { required: string; incorrectPin: string }) =>
  z.string().regex(/^[0-9]{6}$/, t.incorrectPin);

export const birthday = (t: { required: string; tooYoung: string }) =>
  z.date().max(sub(new Date(), { years: 18 }), t.tooYoung);

export const walletEth = (checkWallet: (value: string) => Promise<boolean>) =>
  z.string().refine(async (value) => {
    if (!value) return true;
    return await checkWallet(value);
  }, 'Invalid Eth Wallet');

export interface EmailValidationT {
  invalidEmail: string;
  required: string;
}

export const email = (t: EmailValidationT) =>
  z.string({ required_error: t.required }).email(t.invalidEmail);

export interface PhoneOptionalValidationT {
  invalidPhone: string;
}

export const phoneOptional = (t: PhoneOptionalValidationT) =>
  z
    .object({
      code: z.string(),
      number: z.string(),
    })
    .superRefine((value, context) => {
      if (value?.code === '' && value.number) {
        context.addIssue({
          path: ['code'],
          message: t.invalidPhone,
          code: z.ZodIssueCode.custom,
        });
      }
      if (value?.number === '' && value.code) {
        context.addIssue({
          path: ['number'],
          message: t.invalidPhone,
          code: z.ZodIssueCode.custom,
        });
      }
    });

export const phoneRequired = (t: { phoneCodeRequired: string; phoneNumberLength: string }) =>
  z.object({
    code: z.string().min(1, { message: t.phoneCodeRequired }),
    number: z.string().min(4, { message: t.phoneNumberLength.replace('{{number}}', '4') }),
  });
